/**
 * DotFieldLoader — «зерно будущей картинки» вместо спиннера: фиксированная
 * решётка точек на всю рамку результата, по которой медленно бродит мягкое
 * пятно активности (точки в нём крупнее и плотнее, к краям — усыхают).
 *
 * Почему так (разбор референса владельцем 2026-08-17): анимация не изображает
 * «загрузку», она изображает пиксели будущего кадра — читается как «картинка
 * проявляется», а не «система висит». Спиннер/лого такого не дают.
 *
 * Решётка НЕ движется — модулируются только радиус и прозрачность точки.
 * Поле — сумма трёх синусов с несоизмеримыми периодами: пятно блуждает
 * органически, без видимого «свипа в одну сторону» и без повторяющегося цикла
 * на глаз (шум-библиотека ради этого не нужна).
 *
 * Цвет НЕ хардкожен: три стоп-цвета и потолок непрозрачности приходят из
 * CSS-переменных самого canvas (_dotfield.scss), JS собирает из них наш
 * брендовый градиент 118.5deg — тот же, что у .gradient-frame и логотипа.
 * Перечитываем раз в ~1с, поэтому смена темы подхватывается без слушателей.
 *
 * У поля ДВА состояния. idle — слот ещё пуст: та же решётка и тот же градиент,
 * но статичная и заметная: пустую рамку надо чем-то объяснить. Живое поле
 * берёт ту же геометрию (gridOf), поэтому точки стоят в тех же местах и старт
 * генерации решётку не переставляет; но плотность там падает до FLOOR —
 * целиком решётка в работе не читается, видно только налитые области.
 */
import { useEffect, useRef } from 'react'

interface Props {
	/** Сколько точек по короткой стороне. По умолчанию считается от РАЗМЕРА
	 *  блока (см. dotsFor): в плитке 44px и в зоне 470px решётка должна
	 *  выглядеть одинаково уместно, а не «горохом» в одной и «пылью» в другой.
	 *  Задавать вручную стоит только когда авто-подбор явно мимо. */
	density?: number
	/** Квадрат size×size вместо растяжения на весь родитель. Нужен там, где
	 *  лоадер стоит ЗНАЧКОМ в колонке с подписью (карточка товара, слот
	 *  презентации, большая зона результата): поле занимает ровно габарит
	 *  прежнего лого и не ломает раскладку блока. */
	size?: number
	/** Слот ещё ПУСТ: та же решётка, но спящая — точки стоят на своих местах с
	 *  минимальным радиусом и нейтральным цветом, поле не анимируется. Старт
	 *  генерации не подменяет плейсхолдер другим объектом, а будит этот: точки
	 *  те же и там же, меняются только цвет, радиус и прозрачность — переходу
	 *  нечему мигать (выбор владельца 22.09 из шести вариантов пустого слота). */
	idle?: boolean
	className?: string
}

/** Число точек по короткой стороне блока: шаг решётки держим около 14px,
 *  но не даём ей выродиться — не меньше 8 точек в самой мелкой плитке и не
 *  больше 24 в крупной зоне (иначе поле превращается в шум).
 *  Нижняя граница именно 8: при 6 в тайле 50px сгусток накрывал почти весь
 *  блок и читалась «горстка точек», а не зерно кадра (сверка размеров
 *  50/56/84/120/200/437 на одном листе, 2026-08-17). */
function dotsFor(short: number) {
	return Math.max(8, Math.min(24, Math.round(short / 14)))
}

/** Темп блуждания пятна — ОДНА ручка на всё поле: множитель времени, поэтому
 *  несоизмеримые частоты сохраняют свои отношения и траектория по-прежнему не
 *  замыкается на глаз. 1.25 = «чуть живее» по просьбе владельца 22.09; выше
 *  1.5 движение начинает читаться как рывки на наших 30 fps. */
const SPEED = 1.25

/** Радиус точки в долях шага решётки: минимум вне пятна и прибавка в пике.
 *  Диаметр даже в пике около трети шага: крупнее читается как «пузыри», а не
 *  зерно кадра (владелец, 2026-08-17). */
const DOT_MIN = 0.05
const DOT_RANGE = 0.12

/** Нижняя граница непрозрачности точки в ЖИВОМ поле. Намеренно много ниже
 *  плотности покоя (--df-idle-a): во время генерации решётка целиком читаться
 *  не должна, видно только налитые области — в этом вся идея «зерна кадра»
 *  (владелец 22.09, после моей попытки приравнять покой и работу). */
const FLOOR = 0.12

/** Радиус точки спящей решётки — крупнее тихой точки живого поля: пустой слот
 *  должен читаться сам по себе, а не намекать на себя. */
const DOT_IDLE = 0.07

export default function DotFieldLoader({ density, size, idle, className }: Props) {
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d')
		if (!ctx) return

		const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

		let w = 0
		let h = 0
		let dpr = 1
		// Фирменные стопы и здесь: measure() собирает градиент ДО первого
		// readPaint(), и с чужими значениями первый кадр ушёл бы мимо бренда.
		let stops = ['#35d7ff', '#7b61ff', '#ff4fd8']
		let peak = 0.85
		// Плотность решётки в ПОКОЕ (пустой слот). К живому полю отношения не
		// имеет: там своя, много меньшая нижняя граница — FLOOR.
		let idleAlpha = 0.3
		let paintAge = 0
		let grad: CanvasGradient | null = null

		/** Брендовый градиент 118.5deg на всю рамку — CSS-угол (0deg = вверх,
		 *  по часовой), поэтому вектор направления = (sin a, −cos a). */
		const makeGradient = () => {
			const a = (118.5 * Math.PI) / 180
			const vx = Math.sin(a)
			const vy = -Math.cos(a)
			const len = Math.abs(w * vx) + Math.abs(h * vy)
			const g = ctx.createLinearGradient(
				w / 2 - (vx * len) / 2,
				h / 2 - (vy * len) / 2,
				w / 2 + (vx * len) / 2,
				h / 2 + (vy * len) / 2,
			)
			g.addColorStop(0, stops[0])
			g.addColorStop(0.55, stops[1])
			g.addColorStop(1, stops[2])
			return g
		}

		const readPaint = () => {
			const cs = getComputedStyle(canvas)
			const v = (n: string, fallback: string) => cs.getPropertyValue(n).trim() || fallback
			stops = [v('--df-c1', stops[0]), v('--df-c2', stops[1]), v('--df-c3', stops[2])]
			peak = parseFloat(v('--df-peak', String(peak))) || peak
			idleAlpha = parseFloat(v('--df-idle-a', String(idleAlpha))) || idleAlpha
			grad = makeGradient()
		}

		const measure = () => {
			const rect = canvas.getBoundingClientRect()
			dpr = Math.min(window.devicePixelRatio || 1, 2)
			w = Math.max(1, Math.round(rect.width))
			h = Math.max(1, Math.round(rect.height))
			canvas.width = Math.round(w * dpr)
			canvas.height = Math.round(h * dpr)
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
			// Градиент привязан к размеру кадра — пересобираем вместе с ним.
			grad = makeGradient()
		}

		/** Позиции двух сгустков на момент t (нормированные 0..1).
		 *  Несоизмеримые частоты → траектория не замыкается на глаз. */
		const blobs = (t: number) => [
			{
				cx: 0.5 + 0.3 * Math.sin(t * 0.37) + 0.12 * Math.sin(t * 0.91),
				cy: 0.5 + 0.3 * Math.cos(t * 0.29) + 0.12 * Math.sin(t * 0.67),
				r: 0.52,
				w: 1,
			},
			{
				cx: 0.5 + 0.34 * Math.sin(t * 0.23 + 2.1),
				cy: 0.5 + 0.32 * Math.cos(t * 0.41 + 0.7),
				r: 0.4,
				w: 0.55,
			},
		]

		/** Активность точки (x, y — нормированные) → 0..1.
		 *  Сгустки + мелкая рябь: внутри пятна «зерно», а не ровный круг. */
		const field = (
			x: number,
			y: number,
			t: number,
			bs: ReturnType<typeof blobs>,
			sx: number,
			sy: number,
		) => {
			let v = 0
			for (const b of bs) {
				// Расстояние в единицах КОРОТКОЙ стороны — пятно круглое и в
				// 9:16, и в 16:9 (без этого в портрете получался бы эллипс).
				const dx = (x - b.cx) * sx
				const dy = (y - b.cy) * sy
				const d = Math.sqrt(dx * dx + dy * dy) / b.r
				if (d < 1) {
					const k = 1 - d
					v += b.w * k * k * (3 - 2 * k)
				}
			}
			v += 0.16 * Math.sin(x * 7.3 + t * 0.8) * Math.sin(y * 6.1 - t * 0.6)
			return Math.max(0, Math.min(1, v))
		}

		/** Геометрия решётки для текущего размера — ОБЩАЯ у живого поля и у
		 *  спящего. В этом весь смысл спящего состояния: точки обязаны стоять
		 *  в тех же местах, иначе при старте генерации решётка «переедет» и
		 *  переход снова станет подменой одной картинки другой. */
		const gridOf = () => {
			// Шаг решётки — от короткой стороны: в узкой (9:16) и в широкой
			// рамке точка остаётся одного физического размера. Плотность по
			// умолчанию подбирается под размер блока (dotsFor), поэтому мелкая
			// плитка получает мелкую решётку, а не 3-4 крупные точки.
			const short = Math.min(w, h)
			let pitch = Math.min(24, Math.max(5, short / (density ?? dotsFor(short))))
			// Потолок числа точек: на большом мониторе зона результата может
			// быть 2400×1400, и при шаге 24px это ~5800 дуг КАЖДЫЙ кадр. Ниже
			// порога ничего не меняется, выше — разреживаем решётку.
			const MAX_DOTS = 4000
			const cells = (w / pitch) * (h / pitch)
			if (cells > MAX_DOTS) pitch *= Math.sqrt(cells / MAX_DOTS)
			const cols = Math.ceil(w / pitch)
			const rows = Math.ceil(h / pitch)
			// Центрируем решётку: по краям остаются равные поля, сетка не
			// «прилипает» к левому верхнему углу.
			return {
				short,
				pitch,
				cols,
				rows,
				ox: (w - (cols - 1) * pitch) / 2,
				oy: (h - (rows - 1) * pitch) / 2,
			}
		}

		/** Спящая решётка пустого слота: тот же шаг, те же координаты и ТОТ ЖЕ
		 *  брендовый градиент, что у живого поля, — но ярче и крупнее его тихих
		 *  точек. Покой обязан читаться (иначе слот просто пуст), а работа —
		 *  нет: там решётка уходит в фон и видно только бродящее пятно.
		 *  Рисуется один раз — анимировать покой нечем. */
		const drawIdle = () => {
			if (!w || !h) return
			readPaint()
			const { pitch, cols, rows, ox, oy } = gridOf()
			ctx.clearRect(0, 0, w, h)
			ctx.fillStyle = grad ?? stops[1]
			ctx.globalAlpha = idleAlpha
			for (let r = 0; r < rows; r++) {
				for (let c = 0; c < cols; c++) {
					ctx.beginPath()
					ctx.arc(ox + c * pitch, oy + r * pitch, pitch * DOT_IDLE, 0, Math.PI * 2)
					ctx.fill()
				}
			}
			ctx.globalAlpha = 1
		}

		const draw = (timeMs: number) => {
			if (!w || !h) return
			if (paintAge <= 0) {
				readPaint()
				paintAge = 30
			}
			paintAge--

			ctx.clearRect(0, 0, w, h)
			const { short, pitch, cols, rows, ox, oy } = gridOf()
			const t = (timeMs / 1000) * SPEED
			const bs = blobs(t)
			// Масштабы нормированных координат в единицах короткой стороны.
			const sx = w / short
			const sy = h / short
			ctx.fillStyle = grad ?? stops[1]

			for (let r = 0; r < rows; r++) {
				for (let c = 0; c < cols; c++) {
					const x = ox + c * pitch
					const y = oy + r * pitch
					const f = field(c / Math.max(1, cols - 1), r / Math.max(1, rows - 1), t, bs, sx, sy)
					// Диаметр даже в пике — около трети шага: крупнее решётка
					// читается как «пузыри», а не как зерно кадра (правка
					// владельца 2026-08-17). Про нижнюю границу альфы — FLOOR.
					const radius = pitch * (DOT_MIN + DOT_RANGE * f)
					ctx.globalAlpha = FLOOR + (peak - FLOOR) * f
					ctx.beginPath()
					ctx.arc(x, y, radius, 0, Math.PI * 2)
					ctx.fill()
				}
			}
			ctx.globalAlpha = 1
		}

		measure()
		if (idle) drawIdle()
		else draw(0)

		let raf = 0
		let last = 0
		const loop = (ts: number) => {
			// ~30 fps: движение и так ленивое, 60 кадров жгли бы батарею зря.
			if (ts - last >= 33) {
				last = ts
				draw(ts)
			}
			raf = requestAnimationFrame(loop)
		}
		if (!reduced && !idle) raf = requestAnimationFrame(loop)

		const ro = new ResizeObserver(() => {
			measure()
			if (idle) drawIdle()
			else draw(last)
		})
		ro.observe(canvas)

		// Фон вкладки: кадры не нужны, но при возврате поле должно быть живым.
		const onVis = () => {
			if (reduced || idle) return
			cancelAnimationFrame(raf)
			if (!document.hidden) raf = requestAnimationFrame(loop)
		}
		document.addEventListener('visibilitychange', onVis)

		return () => {
			cancelAnimationFrame(raf)
			ro.disconnect()
			document.removeEventListener('visibilitychange', onVis)
		}
	}, [density, idle])

	return (
		<canvas
			ref={canvasRef}
			className={`dotfield${className ? ` ${className}` : ''}`}
			style={size ? { width: size, height: size, flex: '0 0 auto' } : undefined}
			aria-hidden="true"
		/>
	)
}
